import React from 'react';
import { Chip, Stack } from '@mui/material';
import {
  ChecklistItem, ReferralType, TripMatchStatus, TripRequestStatus,
} from '@marageti/z4-sdk/lib/travel';
import {
  TravelerTagChips,
  TripMatchChips,
  OtherChips,
  TripRequestChips,
} from '@marageti/z4-lib';
import { Traveler, TravelerTags } from '@marageti/z4-sdk/lib/people';
import {
  isBefore, isAfter, differenceInDays, isEqual,
} from 'date-fns';

const {
  ChipPending,
  ChipAccepted,
  ChipPartnerDeclined,
  ChipActive,
  ChipSold: TmChipSold,
  ChipPostponed: TmChipPostponed,
  ChipInactive,
  ChipTravelerCanceled,
  ChipPartnerCanceled,
  ChipZicassoCanceled,
  ChipSoldCanceled,
  ChipRefundedCanceled,
} = TripMatchChips;

const TripMatchStatusChip = ({ status }: { status: TripMatchStatus }) => {
  switch (status) {
    case TripMatchStatus.MatchPending:
      return <ChipPending />;
    case TripMatchStatus.MatchAccepted:
      return <ChipAccepted />;
    case TripMatchStatus.MatchPartnerDeclined:
      return <ChipPartnerDeclined />;
    case TripMatchStatus.MatchActive:
      return <ChipActive />;
    case TripMatchStatus.MatchSold:
      return <TmChipSold />;
    case TripMatchStatus.MatchPostponed:
      return <TmChipPostponed />;
    case TripMatchStatus.MatchInactive:
      return <ChipInactive />;
    case TripMatchStatus.MatchTravelerCanceled:
      return <ChipTravelerCanceled />;
    case TripMatchStatus.MatchPartnerCanceled:
      return <ChipPartnerCanceled />;
    case TripMatchStatus.MatchZicassoCanceled:
      return <ChipZicassoCanceled />;
    case TripMatchStatus.MatchSoldCanceled:
      return <ChipSoldCanceled />;
    case TripMatchStatus.MatchRefundedCanceled:
      return <ChipRefundedCanceled />;
    default:
      return null;
  }
};

const {
  ChipPending: TrChipPending,
  ChipVerified,
  ChipPostponed: TrChipPostponed,
  ChipMatched,
  ChipSold: TrChipSold,
  ChipCanceled,
  ChipJunk,
  ChipReferred,
} = TripRequestChips;

const TripRequestStatusChip = ({ status }: { status: TripRequestStatus }) => {
  switch (status) {
    case TripRequestStatus.PendingStatus:
      return <TrChipPending />;
    case TripRequestStatus.VerifiedStatus:
      return <ChipVerified />;
    case TripRequestStatus.PostponedStatus:
      return <TrChipPostponed />;
    case TripRequestStatus.MatchedStatus:
      return <ChipMatched />;
    case TripRequestStatus.SoldStatus:
      return <TrChipSold />;
    case TripRequestStatus.CanceledStatus:
      return <ChipCanceled />;
    case TripRequestStatus.JunkStatus:
      return <ChipJunk />;
    case TripRequestStatus.ReferredStatus:
      return <ChipReferred />;
    default:
      return null;
  }
};

const {
  ChipPRL,
  ChipRepeat,
} = OtherChips;
const {
  ChipVIP,
  ChipJunk: TravelerChipJunk,
  ChipBadApple,
  ChipRedFlag,
  ChipTravelAgent,
  ChipFOBY,
} = TravelerTagChips;

const TravelerChips = ({
  travelers,
  referralType,
  repeatTraveler,
}: {
  travelers: Traveler[];
  referralType: ReferralType,
  repeatTraveler: boolean,
}) => {
  const primaryTraveler = travelers[0];
  const chips = [];

  if (primaryTraveler.tags) {
    primaryTraveler.tags.forEach((tag: string) => {
      chips.push(tag);
    });
  }

  if (referralType) {
    chips.push(referralType);
  }

  if (repeatTraveler) {
    chips.push('REPEAT_TRAVELER');
  }

  if (chips?.length === 0) {
    return null;
  }

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
    >
      {chips?.map((chip: string, index: number) => {
        const key = `${chip}-${index}`;
        switch (chip) {
          case ReferralType.OneTimePartnerReferral:
          case ReferralType.LifetimePartnerReferral:
            return <ChipPRL key={key} />;
          case TravelerTags.VIP:
            return <ChipVIP key={key} />;
          case TravelerTags.BAD_APPLE:
            return <ChipBadApple key={key} />;
          case TravelerTags.RED_FLAG:
            return <ChipRedFlag key={key} />;
          case TravelerTags.TRAVEL_AGENT:
            return <ChipTravelAgent key={key} />;
          case TravelerTags.JUNK:
            return <TravelerChipJunk key={key} />;
          case TravelerTags.FOBY:
            return <ChipFOBY key={key} />;
          case 'REPEAT_TRAVELER':
            return <ChipRepeat key={key} />;
          default:
            return null;
        }
      })}
    </Stack>
  );
};

const RefineSoldChip = React.memo(({
  checklist,
  departureDate,
  returnDate,
  status,
}: {
  checklist: string[],
  departureDate: string,
  returnDate: string,
  status: TripMatchStatus,
}) => {
  const now = new Date();
  const dDate = new Date(departureDate);
  const rDate = new Date(returnDate);
  const isPreTravel = isBefore(now, dDate);
  const needsReviewRequest = checklist?.includes(ChecklistItem.NeedsZicassoReviewRequest) && isAfter(now, rDate) && differenceInDays(now, rDate) <= 91;
  const isInCountry = (isAfter(now, dDate) || isEqual(now, dDate)) && (isBefore(now, rDate) || isEqual(now, rDate));
  const waitingForReview = checklist?.includes(ChecklistItem.NeedsZicassoReview) && isAfter(now, rDate) && differenceInDays(now, rDate) <= 91;

  if (status === TripMatchStatus.MatchSold) {
    if (isPreTravel) {
      return <Chip variant="outlined" size="small" label="PRE TRAVEL" />;
    }

    if (needsReviewRequest) {
      return <Chip variant="outlined" size="small" label="REQUEST REVIEW" />;
    }

    if (isInCountry) {
      return <Chip variant="outlined" size="small" label="IN COUNTRY" />;
    }

    if (waitingForReview) {
      return <Chip variant="outlined" size="small" label="WAITING FOR REVIEW" />;
    }
  }

  return null;
});

export {
  TripMatchStatusChip,
  TripRequestStatusChip,
  TravelerChips,
  RefineSoldChip,
};
